import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { toast } from 'react-toastify';
import { useKeycloak } from './KeycloakContext';
import { parseISO, format, getISOWeek, getYear, addDays } from 'date-fns';
import { it } from 'date-fns/locale';
import Sidebar from './components/Sidebar';
import TimeSlotModal from './components/TimeSlotModal';
import LogModal from './components/LogModal';
import {
  getLaboratorioUtente,
  createEsperimento,
  updateEsperimento,
  updateLaboratorio,
  uploadFile,
  deleteEsperimento,
  updateTimeSlots,
  deleteDisponibilita
} from './api/api.private.service';
import { getMediaLink } from './api/utils';

const LabManagementPage = () => {
  const [lab, setLab] = useState(null);
  const [editingLab, setEditingLab] = useState(null);
  const [editingExp, setEditingExp] = useState(null);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeSlots, setTimeSlots] = useState({});
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showEditLabModal, setShowEditLabModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [previewExperimentImage, setPreviewExperimentImage] = useState(null);
  const [changes, setChanges] = useState([]);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [originalTimeSlots, setOriginalTimeSlots] = useState({});
  const [copySourceWeek, setCopySourceWeek] = useState(() => {
    const now = new Date();
    const year = format(now, 'yyyy');
    const week = String(getISOWeek(now)).padStart(2, '0');
    return `${year}-W${week}`;
  });
  const [copyTargetWeek, setCopyTargetWeek] = useState(copySourceWeek);

  useEffect(() => {
    const key = format(selectedDate, 'yyyy-MM-dd');
  }, [selectedDate, timeSlots]);

  const fetchLaboratorioUtente = async () => {
    const res = await getLaboratorioUtente();
    if (!res.data || res.data.length === 0) {
      toast.info("Nessun laboratorio associato all'utente");
      setLab(null);
      setEditingLab(null);
      setSelectedExperiment(null);
      setEditingExp(null);
      return null;
    }
    const labObj = res.data[0];  
    setLab(labObj);
    setEditingLab({ ...labObj });
    return labObj;
  };

  useEffect(() => {
    fetchLaboratorioUtente();
  }, []);

  const saveImage = async () => {
    const formData = new FormData();
    formData.append('files', editingLab.immagine);
    formData.append('refId', lab.id);
    formData.append('ref', 'api::laboratori.laboratori');
    formData.append('field', 'immagine');
    await uploadFile(formData);
  };

  const handleExperimentImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditingExp({ ...editingExp, immagine: file });
      setPreviewExperimentImage(URL.createObjectURL(file));
    }
  };

  const handleCloseEditLabModal = () => {
    setEditingLab({ ...lab });
    setPreviewImage(null);
    setShowEditLabModal(false);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditingLab({ ...editingLab, immagine: file });
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleExperimentClick = (experiment) => {
    if (changes.length > 0) {
      toast.error('Termina le modifiche prima di cambiare esperimento.');
      return;
    }
    const initialSlots = experiment.disponibilita.slots || {};

    setOriginalTimeSlots(JSON.parse(JSON.stringify(initialSlots)));
  
    setTimeSlots(initialSlots);

    setSelectedExperiment(experiment);
    setEditingExp(experiment);

    setPreviewExperimentImage(null);
  };

  const handleExperimentSave = async () => {
    const isUpdate = Boolean(selectedExperiment);
    setIsSaving(true);

    try {
      let exp;

      if (isUpdate) {
        await updateExperiment();
        exp = selectedExperiment;
        toast.success('Esperimento modificato con successo!');
      } else {
        exp = await createExperiment();
        toast.success('Esperimento creato con successo!');
      }

      const freshLab = await fetchLaboratorioUtente();

      setSelectedExperiment(null);
      setEditingExp(null);
      setTimeSlots({});
      setOriginalTimeSlots({});
      setChanges([]);
      setPreviewExperimentImage(null);

    } catch (e) {
      console.error(`Errore nella ${isUpdate ? 'modifica' : 'creazione'}:`, e);
      toast.error(`Errore nella ${isUpdate ? 'modifica' : 'creazione'} dell'esperimento!`);
    } finally {
      setIsSaving(false);
    }
  };

  const updateExperiment = async () => {
    await updateEsperimento(editingExp.documentId, {
      titolo: editingExp.titolo,
      descrizione: editingExp.descrizione
    });

    if (previewExperimentImage) {
      const formData = new FormData();
      formData.append('files', editingExp.immagine);
      formData.append('refId', editingExp.id);
      formData.append('ref', 'api::esperimenti.esperimenti');
      formData.append('field', 'immagine');
      await uploadFile(formData);
    }
  };

  const createExperiment = async () => {
    const data = await createEsperimento({
      titolo: editingExp.titolo,
      descrizione: editingExp.descrizione
    });

    await updateLaboratorio(lab.documentId, {
      esperimentis: { connect: [data.id] }
    });

    if (previewExperimentImage && editingExp.immagine) {
      const fd = new FormData();
      fd.append('files', editingExp.immagine);
      fd.append('refId', data.id);
      fd.append('ref', 'api::esperimenti.esperimenti');
      fd.append('field', 'immagine');
      await uploadFile(fd);
    }

    const freshLab = await fetchLaboratorioUtente();
    const expFresh = freshLab.esperimentis.find(e => e.id === data.id);

    await updateTimeSlots(
      expFresh.disponibilita.documentId,
      { slots: timeSlots }
    );

    return expFresh;
  };

  const handleExperimentDelete = async () => {
    setIsSaving(true);
    try {
      await deleteEsperimento(selectedExperiment.documentId);
      const dispoId = selectedExperiment.disponibilita?.documentId;
      if (dispoId) {
        await deleteDisponibilita(dispoId);
      }
      await fetchLaboratorioUtente();
      toast.success('Esperimento eliminato con successo!');

      setSelectedExperiment(null);
      setEditingExp(null);
      setTimeSlots({});
      setOriginalTimeSlots({});
      setChanges([]);
      setPreviewExperimentImage(null);

    } catch (e) {
      console.error('Errore nell\'eliminazione dell\'esperimento:', e);
      toast.error('Errore nell’eliminazione dell’esperimento!');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddExperiment = () => {
    if (changes.length > 0) {
      toast.error(
        'Termina le modifiche prima di creare un nuovo esperimento.'
      );
      return;
    }
    const newExperiment = { titolo: '', descrizione: '' };
    setSelectedExperiment(null);
    setEditingExp(newExperiment);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleRemoveChange = (idx) => {
    setChanges(prev => {
      const change = prev[idx];
      const newTimeSlots = { ...timeSlots };

      if (change.type === 'deleted') {
        newTimeSlots[change.dateKey] = newTimeSlots[change.dateKey]
          ? [...newTimeSlots[change.dateKey], change.slot]
          : [change.slot];

      } else if (change.type === 'added') {
        newTimeSlots[change.dateKey] = newTimeSlots[change.dateKey]
          .filter(s => s !== change.slot);
        if (newTimeSlots[change.dateKey].length === 0) {
          delete newTimeSlots[change.dateKey];
        }
      }

      setTimeSlots(newTimeSlots);

      return prev.filter((_, i) => i !== idx);
    });
  };

  function parseSlot(slotStr, referenceDate) {
    const [startStr, endStr] = slotStr.split(' – ');
    const [sh, sm] = startStr.split(':').map(Number);
    const [eh, em] = endStr.split(':').map(Number);

    const start = new Date(referenceDate);
    start.setHours(sh, sm, 0, 0);
    const end = new Date(referenceDate);
    end.setHours(eh, em, 0, 0);
    return { start, end };
  }

  function isOverlapping(a, b) {
    return a.start < b.end && b.start < a.end;
  }

  const handleAddTimeSlot = ({ startTime, endTime }) => {
    const dateKey = format(selectedDate, 'yyyy-MM-dd');

    const [hStart, mStart] = startTime.split(':').map(Number);
    const [hEnd,   mEnd  ] = endTime.split(':').map(Number);

    const existingSlots = timeSlots[dateKey] || [];
    const existingIntervals = existingSlots.map(s => parseSlot(s, selectedDate));

    let current = new Date(selectedDate);
    current.setHours(hStart, mStart, 0, 0);
    const endDate = new Date(selectedDate);
    endDate.setHours(hEnd, mEnd, 0, 0);

    const allNewSlots = [];
    while (current < endDate) {
      const next = new Date(current);
      next.setHours(next.getHours() + 1);

      const slotStr = `${format(current, 'HH:mm')} – ${format(next, 'HH:mm')}`;
      allNewSlots.push(slotStr);

      current = next;
    }

    const filteredNewSlots = allNewSlots.filter(slotStr => {
      const newInterval = parseSlot(slotStr, selectedDate);
      return !existingIntervals.some(ex => isOverlapping(ex, newInterval));
    });

    if (filteredNewSlots.length === 0) {
      toast.info('Nessuna nuova finestra di prenotazione valida.');
      setShowTimeSlotModal(false);
      return;
    }

    setTimeSlots(prev => ({
      ...prev,
      [dateKey]: prev[dateKey]
        ? [...prev[dateKey], ...filteredNewSlots]
        : [...filteredNewSlots]
    }));

    setChanges(prev => [
      ...prev,
      ...filteredNewSlots.map(slot => ({ dateKey, slot, type: 'added' }))
    ]);

    setShowTimeSlotModal(false);
  };

  const handleOpenChangesModal = () => {
    setShowChangesModal(true);
  };

  const handleCancelChanges = () => {
    const restored = JSON.parse(JSON.stringify(originalTimeSlots));
    setTimeSlots(restored);

    setChanges([]);

    setShowChangesModal(false);
  };
  
  const handleCloseChangesModal = () => {
    setShowChangesModal(false);
  };
  
  const handleConfirmChanges = async () => {
    setIsSaving(true);
    let saved = false;

    try {
      const dispoDocId = selectedExperiment.disponibilita.documentId;
      await updateTimeSlots(dispoDocId, { slots: timeSlots });
      toast.success('Time-slot salvati con successo!');
      saved = true;
    } catch (e) {
      console.error('Errore nel salvataggio dei time-slot:', e);
      toast.error('Errore nel salvataggio dei time-slot!');
    } finally {
      if (saved) {
        try {
          await fetchLaboratorioUtente();
        } catch (e) {
          console.warn('Fetch laboratorio dopo salvataggio fallito:', e);
        }
      }
      setIsSaving(false);
      setChanges([]);
      setShowChangesModal(false);
    }
  };



  const handleDeleteTimeSlot = (dateKey, index) => {
    const slotStr = timeSlots[dateKey][index];

    setTimeSlots(prev => {
      const slots = prev[dateKey].filter((_, i) => i !== index);
      const newTimeSlots = { ...prev };
      if (slots.length > 0) {
        newTimeSlots[dateKey] = slots;
      } else {
        delete newTimeSlots[dateKey];
      }
      return newTimeSlots;
    });

    setChanges(prev => {
      const wasAdded = prev.some(
        c => c.dateKey === dateKey && c.slot === slotStr && c.type === 'added'
      );
      if (wasAdded) {
        return prev.filter(
          c => !(c.dateKey === dateKey && c.slot === slotStr && c.type === 'added')
        );
      } else {
        return [...prev, { dateKey, slot: slotStr, type: 'deleted' }];
      }
    });
  };

  const handleCopyWeekByWeek = () => {
    if (!copySourceWeek || !copyTargetWeek) {
      toast.error('Seleziona sia la settimana di origine che quella di destinazione.');
      return;
    }

    const [srcYearStr, srcW] = copySourceWeek.split('-W');
    const [tgtYearStr, tgtW] = copyTargetWeek.split('-W');
    const srcYear = Number(srcYearStr), srcWeek = Number(srcW);
    const tgtYear = Number(tgtYearStr), tgtWeek = Number(tgtW);

    const entries = Object.entries(timeSlots).filter(([dateKey]) => {
      const d = parseISO(dateKey);
      return getYear(d) === srcYear && getISOWeek(d) === srcWeek;
    });

    if (entries.length === 0) {
      toast.info(`Nessuna disponibilità trovata nella settimana ${copySourceWeek}.`);
      return;
    }

    const weekDiff = tgtWeek - srcWeek;

    setTimeSlots(prev => {
      const merged = { ...prev };
      const newChanges = [];

      entries.forEach(([dateKey, slots]) => {
        const original   = parseISO(dateKey);
        const targetDate = addDays(original, weekDiff * 7);
        const newKey     = format(targetDate, 'yyyy-MM-dd');

        const existing = merged[newKey] || [];

        const toAdd = slots.filter(slot => !existing.includes(slot));
        if (toAdd.length === 0) return; 

        merged[newKey] = [...existing, ...toAdd];

        toAdd.forEach(slot => {
          newChanges.push({ dateKey: newKey, slot, type: 'added' });
        });
      });

      setChanges(prev => [...prev, ...newChanges]);
      return merged;
    });
  };


  const handleSaveLogs = () => {
    fetch('/api/save-logs', {
      method: 'POST',
      body: JSON.stringify(timeSlots),
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then((data) => {
        toast.success('Modifiche salvate con successo!');
        setShowLogModal(false);
      })
      .catch((error) => {
        console.error('Errore nel salvataggio delle modifiche:', error);
        toast.error('Errore nel salvataggio delle modifiche.');
      });
  };

  const handleEditLab = () => {
    setShowEditLabModal(true);
  };

  const handleEditLabSave = async () => {
    setIsSaving(true);
    try {
      const data = await updateLaboratorio(lab.documentId, {
        nome: editingLab.nome,
        descrizione: editingLab.descrizione
      });
      if (previewImage) {
        try {
          await saveImage();
        } catch (e) {
          console.error('Errore nel caricamento del file.', e);
          toast.error('Errore nel caricamento del file.');
        }
      }
      fetchLaboratorioUtente();
      toast.success('Laboratorio modificato con successo!');
      setShowEditLabModal(false);
    } catch (e) {
      console.error('Errore nella modifica del laboratorio:', e);
      toast.error('Errore nella modifica del laboratorio.');
    } finally {
      setIsSaving(false);
    }
  };

  const dateKey = format(selectedDate, 'yyyy-MM-dd');
  const selectedDateSlots = timeSlots[dateKey] || [];
  const formattedDate = format(selectedDate, 'dd MMMM yyyy', { locale: it });

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          'linear-gradient(rgba(255,255,255,.9), rgba(255,255,255,.9)), url("/bg.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <Navbar />

      <div className="flex flex-col items-center p-5">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 inline-block">
            {lab && `Gestione: ${lab.nome}`}
          </h1>
          {lab && (
            <button
              onClick={handleEditLab}
              className="ml-2 text-gray-600 hover:text-gray-800"
            >
              <i className="fas fa-pencil-alt"></i>
            </button>
          )}
        </div>

        {lab && (
          <p className="text-gray-600 mt-2">{lab.descrizione}</p>
        )}

        {lab && (
          <img
            src={getMediaLink(editingLab.immagine?.url)}
            width="300px"
            alt={editingLab.nome}
          />
        )}
      </div>

      <div className="flex flex-col lg:flex-row flex-1 py-10 px-6">
        {lab && (
          <Sidebar
            experiments={lab.esperimentis}
            onExperimentClick={handleExperimentClick}
            onAddExperiment={handleAddExperiment}
            selectedExperiment={selectedExperiment}
          />
        )}

        {(selectedExperiment || editingExp) && (
          <div className="w-full lg:w-2/3 bg-white rounded-lg shadow-md p-4 lg:ml-5 flex flex-col items-center">
            <h2 className="text-2xl font-bold text-gray-800 mt-6 mb-6">
              {selectedExperiment ? 'Modifica Esperimento' : 'Aggiungi Esperimento'}
            </h2>

            <div className="flex flex-col w-full items-center md:flex-row md:items-start">
              <div className="flex flex-col w-1/2 items-center md:px-20">
                <label className="block mb-2 text-lg font-semibold">Titolo</label>
                <input
                  type="text"
                  value={editingExp.titolo}
                  onChange={(e) =>
                    setEditingExp({ ...editingExp, titolo: e.target.value })
                  }
                  className="w-full border p-2 rounded mb-4"
                  disabled={isSaving}
                />

                <label className="block mb-2 text-lg font-semibold">Descrizione</label>
                <textarea
                  value={editingExp.descrizione}
                  onChange={(e) =>
                    setEditingExp({ ...editingExp, descrizione: e.target.value })
                  }
                  className="w-full border p-2 rounded mb-4"
                  rows={5}
                  disabled={isSaving}
                />
              </div>

              <div className="flex flex-col w-1/2 items-center md:px-20">
                <label className="block mb-2 text-lg font-semibold">Immagine</label>
                <input
                  type="file"
                  onChange={handleExperimentImageChange}
                  className="w-full border p-2 rounded mb-4"
                  disabled={isSaving}
                />

                {previewExperimentImage ? (
                  <img
                    src={previewExperimentImage}
                    alt="Anteprima Immagine Esperimento"
                    className="mt-4 rounded-lg shadow-md"
                    width="200"
                  />
                ) : (
                  editingExp.immagine?.url && (
                    <img
                      src={getMediaLink(editingExp.immagine.url)}
                      alt={editingExp.titolo}
                      className="mt-4 rounded-lg shadow-md"
                      width="200"
                    />
                  )
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0 mt-4" >
              <button
                onClick={handleExperimentSave}
                className="inline-flex items-center px-6 py-3 bg-white text-black border-2 border-black uppercase text-sm font-semibold shadow hover:bg-black hover:text-white whitespace-nowrap"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-3" />
                    Salva Esperimento
                  </>
                )}
              </button>

              <button
                onClick={handleExperimentDelete}
                className="inline-flex items-center px-6 py-3 bg-white text-black border-2 border-black uppercase text-sm font-semibold shadow hover:bg-black hover:text-white whitespace-nowrap"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Eliminazione...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash-alt mr-3" />
                    Elimina Esperimento
                  </>
                )}
              </button>
            </div>
            {selectedExperiment && (
              <div className="flex flex-col items-center w-full md:w-1/2 mt-8 lg:w-full">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                  Gestisci Finestre di Prenotazione
                </h2>
                <Calendar onChange={handleDateChange} value={selectedDate} />

                <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0 mt-4">
                  <button
                    onClick={() => setShowTimeSlotModal(true)}
                    className="w-full sm:w-auto flex-1 inline-flex justify-center items-center px-6 py-3 bg-white text-black border-2 border-black uppercase text-sm font-semibold shadow hover:bg-black hover:text-white"
                  >
                    <i className="fas fa-plus mr-3" />
                    Aggiungi Finestre di Prenotazione
                  </button>

                  <button
                    onClick={handleOpenChangesModal}
                    className="w-full sm:w-auto flex-1 inline-flex justify-center items-center px-6 py-3 bg-white text-black border-2 border-black uppercase text-sm font-semibold shadow hover:bg-black hover:text-white"
                  >
                    <i className="fas fa-list mr-3" />
                    Modifiche Apportate
                  </button>
                </div>
                <div className="w-full mt-6 flex flex-col items-center justify-center space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4 lg:items-end">
                  <div className="w-full lg:w-auto flex flex-col items-center" >
                      <label className="block text-sm font-medium">Da settimana</label>
                      <input
                        type="week"
                        value={copySourceWeek}
                        onChange={e => setCopySourceWeek(e.target.value)}
                        className="border p-2 rounded"
                      />
                    </div>
                    <div className="w-full lg:w-auto flex flex-col items-center">
                      <label className="block text-sm font-medium">A settimana</label>
                      <input
                        type="week"
                        value={copyTargetWeek}
                        onChange={e => setCopyTargetWeek(e.target.value)}
                        className="border p-2 rounded"
                      />
                    </div>
                    <div className="w-full lg:w-auto flex flex-col justify-end"> 
                      <button
                        onClick={handleCopyWeekByWeek}
                        className="px-4 py-2 bg-white text-black border-2 border-black uppercase text-sm font-semibold shadow hover:bg-black hover:text-white whitespace-nowrap mb-0.5"
                      >
                        <i className="fas fa-copy mr-2" />
                        Copia settimana per settimana
                      </button> 
                    </div>
                </div>
                {selectedDateSlots.length > 0 && (
                  <div className="mt-6 w-full">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                      Finestre di prenotazione per {formattedDate}
                    </h3>
                    <ul>
                      {selectedDateSlots.map((slot, index) => (
                        <li
                          key={index}
                          className="mb-2 p-4 rounded-xl border border-gray-200 flex justify-between items-center"
                        >
                          <span className="bg-black text-white rounded-xl px-5">
                            {slot}
                          </span>
                          <button
                            onClick={() => handleDeleteTimeSlot(dateKey, index)}
                            className="px-2 py-1 rounded-lg hover:text-[#e06615]"
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
          </div>
        )}

        {lab && !editingExp && (
          <div className="w-full lg:w-2/3 bg-white rounded-lg shadow-md p-4 lg:ml-5 flex flex-col items-center">
            <h2 className="text-2xl font-bold text-gray-800 mt-6 mb-6">
              Seleziona un esperimento per modificarlo
            </h2>
          </div>
        )}

        {!lab && (
          <div className="w-full bg-white rounded-lg shadow-md p-4 lg:ml-5 flex flex-col items-center">
            <h2 className="text-2xl font-bold text-gray-800 mt-6 mb-6 flex flex-col items-center">
              Nessun laboratorio associato all'utente. <br /><a href="https://silabor.it/Contacts" className="text-sky-600 underline">Contatta l'amministratore</a>
            </h2>
          </div>
        )}
      </div>

      <Footer />

      {showTimeSlotModal && (
        <TimeSlotModal
          onClose={() => setShowTimeSlotModal(false)}
          onSave={handleAddTimeSlot}
        />
      )}

      {showLogModal && (
        <LogModal
          logs={timeSlots}
          onClose={() => setShowLogModal(false)}
          onSave={handleSaveLogs}
        />
      )}

      {showEditLabModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Modifica Laboratorio</h2>
            <label className="block mb-2 text-lg font-semibold">Nome</label>
            <input
              type="text"
              value={editingLab.nome}
              onChange={(e) => setEditingLab({ ...editingLab, nome: e.target.value })}
              className="w-full border p-2 rounded mb-4"
              disabled={isSaving} 
            />
            <label className="block mb-2 text-lg font-semibold">Descrizione</label>
            <textarea
              value={editingLab.descrizione}
              onChange={(e) => setEditingLab({ ...editingLab, descrizione: e.target.value })}
              className="w-full border p-2 rounded mb-4"
              disabled={isSaving} 
            ></textarea>
            <label className="block mb-2 text-lg font-semibold">Logo</label>
            <input
              type="file"
              onChange={handleLogoChange}
              className="w-full border p-2 rounded mb-4"
              disabled={isSaving} 
            />
            {previewImage ? (
              <img
                src={previewImage}
                alt="Anteprima Logo"
                className="mt-4 rounded-lg shadow-md"
                width="200"
              />
            ) : (
              <img
                src={getMediaLink(editingLab.immagine?.url)}
                width={'200px'}
                alt={editingLab.nome}
              />
            )}
            
            <div className="flex justify-end">
              <button onClick={handleCloseEditLabModal}
                className={`px-4 py-2 bg-white text-black border-2 border-black uppercase text-sm font-semibold shadow hover:bg-black hover:text-white whitespace-nowrap m-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isSaving}
              >
                Annulla
              </button>
              <button
                onClick={handleEditLabSave}
                className={`px-4 py-2 bg-white text-black border-2 border-black uppercase text-sm font-semibold shadow hover:bg-black hover:text-white whitespace-nowrap m-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isSaving}
              >
                {isSaving ? (
                  <span className="flex items-center">
                    <i className="fas fa-spinner fa-spin mr-2"></i> Salvataggio...
                  </span>
                ) : (
                  'Salva'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showChangesModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center">
          <div className="relative bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <button
              onClick={handleCloseChangesModal}
              className="absolute top-4 left-4 text-gray-500 hover:text-gray-800"
              title="Chiudi senza salvare"
            >
              <i className="fas fa-times"></i>
            </button>

            <h2 className="text-2xl font-bold mb-4 text-center">Modifiche Apportate</h2>

            {changes.length > 0 ? (
              <ul className="mb-6 max-h-60 overflow-auto space-y-2">
                {changes.map(({ dateKey, slot, type }, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between p-2 border rounded"
                  >
                    <div className="flex items-center">
                      <span className="font-semibold mr-2">
                        {format(parseISO(dateKey), 'dd MMMM yyyy', { locale: it })}:
                      </span>
                      {type === 'added' ? (
                        <i className="fas fa-plus text-green-600 mr-1" />
                      ) : (
                        <i className="fas fa-trash-alt text-red-600 mr-1" />
                      )}
                      <span>{slot}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveChange(idx)}
                      className="text-gray-500 hover:text-gray-800 p-1"
                      title="Annulla questa singola modifica"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-6 text-center">Nessuna modifica apportata.</p>
            )}
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCancelChanges}
                className="px-4 py-2 bg-white text-black border-2 border-black uppercase text-sm font-semibold shadow hover:bg-black hover:text-white"
                disabled={isSaving}
              >
                Annulla modifiche
              </button>
              <button
                onClick={handleConfirmChanges}
                className="px-4 py-2 bg-white text-black border-2 border-black uppercase text-sm font-semibold shadow hover:bg-black hover:text-white"
                disabled={isSaving}
              >
                {isSaving
                  ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2" />
                      Salvataggio...
                    </>
                  )
                  : 'Conferma modifiche'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabManagementPage;
